// Convención de agrupado del encabezado común (AppHeader), pedido de Isaac
// 2026-10-20: "el appheader de mantenimiento, todo amontonado; usar para los
// módulos operativos esa lógica de 'Operación' (la de fincas) y que todos se
// vean igual".
//
// Regla: como máximo 4 entradas visibles en la fila, contando los grupos.
// Grupos con nombre e ícono fijos, siempre en este orden cuando aplican:
//   operacion  "Operación"  el trabajo del día: tableros, registros, tareas
//   reportes   "Reportes"   informes, historial, exportar
//   gestion    "Gestión"    catálogos, configuración, personas, permisos
// Gestión va última, a la derecha de Reportes (pedido de Isaac 2026-10-21:
// "en todos los appheader la opción de gestión a la derecha de reportes, que
// tome el último lugar hacia la derecha").
// Antes de los grupos, 1 o 2 entradas sueltas muy usadas ("Inicio", "Hoy").
//
// Uso (React, misma forma de `secciones` que ya recibe AppHeader):
//   import { agruparSecciones } from 'components/ui/agruparSecciones';
//   const NAV = [
//     { to: '/', label: 'Hoy', icon: LayoutDashboard, exact: true },
//     { to: '/agenda', label: 'Agenda', icon: CalendarDays, grupo: 'operacion' },
//     { to: '/historial', label: 'Historial', icon: History, grupo: 'reportes' },
//     { to: '/personas', label: 'Personas', icon: Users, grupo: 'gestion' },
//   ];
//   <AppHeader secciones={agruparSecciones(NAV, { sueltas: ['/'] })} ... />
//
// `sueltas` lista las secciones que quedan fuera de los grupos, por `to` o por
// `label`, y salen en el orden en que aparecen en `secciones`. Un grupo sin
// ítems (porque el rol no ve ninguno) no se muestra.
//
// El ícono del grupo va por NOMBRE ('ClipboardList', 'BarChart3', 'Settings'):
// AppHeader lo traduce al componente de lucide y la variante estática
// (app-header.js) al SVG de lucide. Así este archivo no depende de nada y
// corre igual en Vite, en Next y en una página sin bundler.
//
// No lanza errores: la lista de cada rol se arma en tiempo de ejecución y un
// encabezado no puede tumbar la app. Para pruebas y revisiones está
// revisarAgrupado(), que devuelve los problemas como texto.

export const MAX_ENTRADAS = 4;
export const MAX_SUELTAS = 2;
export const ORDEN_GRUPOS = ['operacion', 'reportes', 'gestion'];
export const GRUPOS = {
  operacion: { label: 'Operación', icon: 'ClipboardList' },
  reportes: { label: 'Reportes', icon: 'BarChart3' },
  gestion: { label: 'Gestión', icon: 'Settings' },
};

const esGrupo = (s) => Array.isArray(s?.items);
const esSuelta = (s, sueltas) => sueltas.includes(s?.to) || sueltas.includes(s?.label);

function sinGrupo(s) {
  const { grupo, ...resto } = s;
  return resto;
}

export function agruparSecciones(secciones = [], { sueltas = [] } = {}) {
  const lista = Array.isArray(secciones) ? secciones.filter(Boolean) : [];
  const primero = [];
  const porGrupo = Object.fromEntries(ORDEN_GRUPOS.map((g) => [g, []]));
  for (const s of lista) {
    if (esGrupo(s)) primero.push(s); // grupo ya armado: se respeta tal cual
    else if (!esSuelta(s, sueltas) && porGrupo[s.grupo]) porGrupo[s.grupo].push(sinGrupo(s));
    else primero.push(sinGrupo(s)); // suelta, o sin grupo válido (revisarAgrupado lo avisa)
  }
  const grupos = ORDEN_GRUPOS.filter((g) => porGrupo[g].length > 0).map((g) => ({
    label: GRUPOS[g].label,
    icon: GRUPOS[g].icon,
    grupo: g,
    items: porGrupo[g],
  }));
  return [...primero, ...grupos];
}

// Problemas de la convención para una lista de secciones (con `grupo`), como
// texto. Lista vacía = cumple. Pensado para una prueba por app:
//   expect(revisarAgrupado(NAV, { sueltas: ['/'] })).toEqual([]);
export function revisarAgrupado(secciones = [], { sueltas = [] } = {}) {
  const problemas = [];
  const lista = Array.isArray(secciones) ? secciones.filter(Boolean) : [];
  for (const s of lista) {
    if (esGrupo(s) || esSuelta(s, sueltas)) continue;
    const nombre = s.label || s.to || '?';
    if (s.grupo === undefined) problemas.push(`"${nombre}" no tiene grupo ni está en sueltas`);
    else if (!GRUPOS[s.grupo]) problemas.push(`"${nombre}" tiene un grupo desconocido: ${s.grupo} (usar ${ORDEN_GRUPOS.join(', ')})`);
  }
  const fila = agruparSecciones(lista, { sueltas });
  const nSueltas = fila.filter((s) => !esGrupo(s)).length;
  if (nSueltas > MAX_SUELTAS) problemas.push(`${nSueltas} entradas sueltas (máximo ${MAX_SUELTAS})`);
  if (fila.length > MAX_ENTRADAS) problemas.push(`${fila.length} entradas en la fila (máximo ${MAX_ENTRADAS}, contando grupos)`);
  return problemas;
}
